'use client';

import {
  FlooringTypes,
  type KeyVal,
  type ProductInformation,
} from '@dealerportal/common';
import { useTranslations } from '@dealerportal/i18n/client';
import { useClientContext } from '@dealerportal/react-helpers';
import { BetterButton } from '@dealerportal/ui-components/BetterButton';
import {
  BroadstreetZoneType,
  remapBrandName,
  toTitleCase,
} from '@dealerportal/utilities';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { useCtaColor } from '../../../../../react-helpers/src/lib/hooks/useCtaColor';
import FeaturedProducts from '../FeaturedProducts/FeaturedProducts';
import { Samples } from '../ProductCard/Samples';
import { FavouritesCount } from '../ProductToolbar/FavouritesCount';
import { AddToCart } from './AddToCart';
import BannerZone from './BannerZone';
import { ContentProximityMillsFeatures } from './ContentProximityMillsFeatures';
import { DownloadLink } from './DownloadLink';
import { Gallery } from './Gallery';
import { ProductSpec } from './ProductSpec';
import { Section } from './Section';
import { WebVisualizer } from './WebVisualizer';

import './ProductDetails.css';
import { KBFrConfigurator } from './KBFrConfigurator';
import { KBFrVisualizer } from './KBFrVisualizer';
import { ProductDetailsContent } from './ProductDetailsContent';
import { ProductDetailsHeader } from './ProductDetailsHeader';

type Props = {
  productInfo: ProductInformation;
  isMobileDevice: boolean;
  topSectionContent: React.ReactNode;
  marketingContent: React.ReactNode;
  actonForm: React.ReactNode;
  hideBrandColorSkuTitleInPDP?: boolean;
};

export const ProductDetails = ({
  productInfo,
  isMobileDevice,
  topSectionContent,
  marketingContent,
  actonForm,
  hideBrandColorSkuTitleInPDP,
}: Props) => {
  const t = useTranslations('products');
  const [specs, setSpecs] = useState<KeyVal[]>(productInfo.specs);
  const ctx = useClientContext();
  const { ctaColor } = useCtaColor();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    ctx.addRecentProduct(productInfo);
  }, []);

  useEffect(() => {
    if (window.location.hash === '#vis') {
      // Small delay to ensure the element is rendered
      setTimeout(() => {
        document
          .getElementById('visualizer')
          ?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, []);

  const vizToken =
    ctx.visualizerSettings !== undefined
      ? ctx.visualizerSettings.showVisualizer
        ? (ctx.visualizerSettings.apiKey ?? '')
        : ''
      : (ctx.tokens.find((x) => x.TokenName === 'ff-viz-api-token')
          ?.Replacement ?? '');
  const colorTones =
    productInfo.specs.find((s) => s.key === 'ColorTones')?.value || null;
  const colorRange =
    productInfo.specs.find((s) => s.key === 'ColorRange')?.value || null;
  const broadstreetKeywords =
    ctx.tokens.find((x) => x.TokenName === 'companyid')?.Replacement ?? '';

  let similarProductsQuery = `type=${productInfo.productTypeSlug ?? productInfo.productType}`;

  if (colorTones) {
    similarProductsQuery += `&color-tones=${encodeURIComponent(colorTones)}`;
  }

  if (colorRange) {
    similarProductsQuery += `&color-range=${encodeURIComponent(colorRange)}`;
  }

  let tags = productInfo?.tags ?? [];
  tags = tags.filter((t) => t !== 'Order_Sample');

  const showOrderSample = productInfo.hasSample && !ctx.disableOrderSample;

  const showKbFrVisualizer = useMemo(() => {
    return !FlooringTypes.includes(productInfo.productType);
  }, [productInfo.productType]);

  return (
    <div>
      <section className="pdp__top-section ui-py-8 ui-pb-12 ui-bg-gray-100">
        <div className="ui-container !ui-max-w-screen-xl">
          <div className="pdp__backlink ui-mb-6 ui-flex ui-items-center ui-justify-between">
            <Link
              className="ui-flex ui-items-center ui-gap-2 !ui-text-sm !ui-text-gray-500 hover:!ui-text-gray-600"
              href={`/p/${(
                productInfo.productTypeSlug ?? productInfo.productType
              )
                .toLowerCase()
                .replace(/ /g, '-')}`}
              aria-label={`Link to page all ${productInfo.productType} flooring`}
              prefetch={false}
            >
              <svg
                className="ui-w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512"
              >
                <path
                  className="ui-fill-gray-500"
                  d="M7.4 273.4C2.7 268.8 0 262.6 0 256s2.7-12.8 7.4-17.4l176-168c9.6-9.2 24.8-8.8 33.9 .8s8.8 24.8-.8 33.9L83.9 232 424 232c13.3 0 24 10.7 24 24s-10.7 24-24 24L83.9 280 216.6 406.6c9.6 9.2 9.9 24.3 .8 33.9s-24.3 9.9-33.9 .8l-176-168z"
                />
              </svg>
              {t('viewAll')} {productInfo.productType}
            </Link>
            <FavouritesCount />
          </div>

          <div className="pdp__top-section-grid ui-min-0 ui-grid md:ui-grid-cols-2 lg:ui-grid-cols-5 ui-gap-6 md:ui-gap-12">
            <div className="pdp__top-section-col-1 lg:ui-col-span-2">
              {isMobileDevice && (
                <ProductDetailsHeader
                  productInfo={productInfo}
                  isMobileDevice={true}
                  hideBrandColorSkuTitleInPDP={hideBrandColorSkuTitleInPDP}
                  tags={tags}
                />
              )}
              <div className="pdp__gallery">
                <Gallery productInfo={productInfo} setSpecs={setSpecs} />
              </div>
            </div>

            <div
              className={
                'pdp__top-section-col-2 ui-min-w-0 lg:ui-col-span-3 md:ui-mt-2 ui-space-y-3'
              }
            >
              {!isMobileDevice && (
                <ProductDetailsHeader
                  productInfo={productInfo}
                  isMobileDevice={false}
                  hideBrandColorSkuTitleInPDP={hideBrandColorSkuTitleInPDP}
                  tags={tags}
                />
              )}

              {/* Pills */}

              <ProductDetailsContent
                productInfo={productInfo}
                specs={specs}
                setSpecs={setSpecs}
              />

              <div className="pdp__ctas ui-flex ui-gap-2 ui-flex-wrap !ui-mt-8">
                {productInfo.hasCommerce &&
                  productInfo.productType === 'Carpet Tile' && (
                    <AddToCart productInfo={productInfo} />
                  )}

                {showOrderSample && (
                  <Samples
                    productInfo={productInfo}
                    samplePrice={productInfo.samplePrice}
                  />
                )}

                {productInfo.hasVisualization && vizToken && (
                  <BetterButton
                    label={t('seeInMyRoom')}
                    labelClassName="ui-text-sm"
                    btnStyle={
                      !showOrderSample && productInfo.hasVisualization
                        ? 'primary'
                        : 'neutral'
                    }
                    attributes={{
                      id: 'see-in-my-room-button',
                      onClick: () => {
                        document
                          .getElementById('visualizer')
                          ?.scrollIntoView({ behavior: 'smooth' });
                      },
                    }}
                  />
                )}

                <BetterButton
                  label={t('requestMoreInfo')}
                  labelClassName="ui-text-sm"
                  btnStyle={
                    !showOrderSample && !productInfo.hasVisualization
                      ? 'primary'
                      : 'neutral'
                  }
                  attributes={{
                    onClick: () => {
                      document
                        .getElementById('request-form')
                        ?.scrollIntoView({ behavior: 'smooth' });
                    },
                  }}
                />
              </div>

              {(productInfo.files?.length ?? 0) > 0 && (
                <div>
                  <DownloadLink
                    files={
                      productInfo.files?.map((file) => ({
                        label: toTitleCase(file.type),
                        file: file.uri,
                      })) ?? []
                    }
                  />
                </div>
              )}
            </div>

            {topSectionContent && (
              <div
                className="pdp__top-section-col-3"
                data-token-generated="PDPContentTopSection"
              >
                {topSectionContent}
              </div>
            )}
          </div>
        </div>
      </section>
      <section className="pdp__middle-section">
        <div className="ui-container !ui-max-w-screen-xl ui-space-y-8 lg:ui-space-y-12 ui-py-8 md:ui-py-12">
          {productInfo.hasVisualization && vizToken && (
            <Section
              id="visualizer"
              title={t('seeInMyRoom')}
              description={t('transformYourRoom')}
              className="pdp__visualizer visualizer-wrapper ui-text-left"
            >
              <WebVisualizer
                DealerAPIKey={vizToken}
                upid={productInfo?.UPID || ''}
                productInfo={productInfo}
                isMobileDevice={isMobileDevice}
                config={{
                  limitroom: true,
                  product: productInfo?.UPID || '',
                  mode: 'full',
                  type: 'sku',
                  accent: ctaColor,
                  languages: ['es', 'en', 'fr', 'it', 'pt'],
                  logo: '',
                  showwelcome: 0,
                  startmenu: 0,
                }}
              />
            </Section>
          )}

          {remapBrandName(productInfo.brandName) === 'Proximity Mills' ? (
            <section className="pdp__fsu ui-pb-8 md:ui-pb-12">
              <div id="broadstreet-pxm-video">
                <BannerZone
                  broadstreetZone={{
                    id: BroadstreetZoneType.PXMVideo,
                    keywords: broadstreetKeywords,
                  }}
                />
              </div>
              {productInfo.productType === 'Luxury Vinyl' ||
              productInfo.productType === 'Hardwood' ? (
                <div className="!ui-max-w-screen-xl ui-mt-[3rem] ui-mb-[1rem]">
                  <div className="!ui-max-w-screen-xl ui-space-y-16">
                    <Section
                      id="pxm-features-benefits"
                      title={t('featuresAndBenefits')}
                      className="ui-text-left"
                    >
                      <ContentProximityMillsFeatures
                        specs={specs}
                        productInfo={productInfo}
                      />
                    </Section>
                  </div>
                </div>
              ) : null}
            </section>
          ) : null}

          {showKbFrVisualizer && (
            <div className="ui-flex">
              <div className="ui-w-1/2">
                <KBFrVisualizer product={productInfo} />
              </div>
              <div className="ui-w-1/2 ui-overflow-y-scroll no-scrollbar">
                <KBFrConfigurator product={productInfo} />
              </div>
            </div>
          )}

          {specs?.length > 0 && (
            <Section title={t('specifications')} className="pdp__specs">
              <ProductSpec specs={specs} />
            </Section>
          )}

          <section className="pdp__content-marketing">
            {marketingContent}
          </section>

          <Section
            data-cy="similar-products"
            title={t('similarProducts')}
            className="pdp__similar-products"
          >
            <FeaturedProducts
              excludeUpid={productInfo?.UPID ?? ''}
              query={similarProductsQuery}
            />
          </Section>
        </div>
      </section>

      <section className="pdp__bottom-section ui-bg-gray-100 ui-py-8 md:ui-py-12">
        <div className="ui-container !ui-max-w-screen-xl">
          <Section
            data-cy="request-form"
            id="request-form"
            className="pdp__form ui-max-w-screen-md"
            title={t('requestMoreInfo')}
          >
            <div>{actonForm}</div>
          </Section>
        </div>
      </section>
    </div>
  );
};
